import { type MenuProps, theme } from "antd"

import { getVerticalScrollOverflow } from "../../utils/vertical-scroll-overflow.ts"

// The menu's own padding, above and below the rows.
const MENU_VERTICAL_PADDING = 8

type MenuItems = MenuProps["items"]

/**
 * Marks the popup as a row's actions menu, wherever it is mounted.
 *
 * The z-cap hangs off this rather than off `.ant-table-body .ant-dropdown`, for
 * two reasons: that selector caught every dropdown portaled into any table body
 * — a column filter, a select in an editable cell — and it stopped applying in
 * the one case that needs it most, a menu that fell back to `document.body` and
 * kept AntD's 1050 over the sticky tabs.
 */
export const ROW_ACTIONS_MENU_CLASS = "row-actions-menu"

/**
 * Rows the menu will render.
 *
 * A group contributes its children, not itself — the group entry is a label the
 * children sit under, so counting it as one row and ignoring them undercounts a
 * grouped menu badly.
 */
const countMenuRows = (items: MenuItems): number =>
  (items ?? []).reduce<number>((rows, item) => {
    const children = (item as { children?: MenuItems } | null)?.children
    return rows + (children?.length ? countMenuRows(children) : 1)
  }, 0)

/**
 * How tall the menu will be once it opens.
 *
 * The container has to be chosen before the popup exists, so the height is
 * counted rather than measured, from the theme's own small-control height
 * rather than a hardcoded one.
 *
 * It deliberately errs *low*. Over-estimating sounds like the cautious
 * direction — it sends a doubtful menu to `document.body`, where nothing crops
 * it — but on `document.body` nothing bounds it either, and that is the state
 * EAD-680 was filed about. Under-estimating risks a crop; over-estimating
 * reproduces the bug. A divider is shorter than a row, so counting it as one
 * absorbs the difference.
 */
export const estimateMenuHeight = (
  items: MenuItems,
  itemHeight: number
): number => countMenuRows(items) * itemHeight + MENU_VERTICAL_PADDING

/**
 * Mount a row's actions menu inside the table's own scroll viewport.
 *
 * On `document.body` the menu is placed once, at viewport coordinates. A table
 * that scrolls its body internally then slides the row out from under it, so
 * the menu is left hanging over whichever row happens to be there — it stops
 * belonging to the row it acts on.
 *
 * Edit-row Select and DatePicker popups portal to `document.body` so the table
 * cannot clip them, and close when their table scrolls horizontally. An actions
 * menu instead lives in the pinned column itself, so it has to travel with its
 * row.
 *
 * Anchoring is conditional, and AntD resolves the container once per open. A
 * table that is short when the menu opens and grows while it is still open
 * therefore keeps `document.body` until the menu is reopened. Accepted: it
 * takes rows arriving without a scroll — a filter or a late load — and costs a
 * stale position on a menu the user can close.
 *
 * The condition is whether the body can *hold* the menu, not whether it
 * scrolls. `scroll.y` is a max-height, so a table under its cap never scrolls,
 * and a body that is nonetheless several times the menu's height clips it
 * perfectly well — while on `document.body` nothing does, and the menu spills
 * past the table's edge and rides over the page's sticky chrome as it scrolls.
 * Only a body shorter than the menu has to give it up.
 *
 * A body shorter than its own menu still exists — a Finance tab with one or two
 * transactions is the ordinary case, not a corner — and no container choice can
 * make a menu fit a table smaller than it is. That case is not fixed here: the
 * popup carries `row-actions-menu`, whose z-cap follows it onto `document.body`,
 * so the menu overhangs below a very short table (as it always did) without
 * riding over the page's sticky chrome.
 *
 * See `docs/kb/decisions/2026-08-31-a-row-menu-is-capped-not-only-clipped.md`.
 */
const getRowActionsPopupContainer = (
  trigger: HTMLElement,
  menuHeight: number
): HTMLElement => {
  const body = trigger.closest(".ant-table-body") as HTMLElement | null
  if (!body) {
    warnIfTableHasNoScrollBody(trigger)
    return document.body
  }

  // Row heights are fractional, so an exactly-fitting body reports a
  // scrollHeight a pixel over its clientHeight.
  if (getVerticalScrollOverflow(body) > 1) return body

  return body.clientHeight >= menuHeight ? body : document.body
}

/**
 * A table renders `.ant-table-body` only when `scroll.y` is set; with `scroll.x`
 * alone it renders one `.ant-table-content` and there is nothing to anchor in.
 * Nothing enforces that at the type level — the column and the table are
 * different files — so say it where it happens, once, rather than pinning it
 * per consumer.
 */
let warnedAboutMissingScrollY = false
const warnIfTableHasNoScrollBody = (trigger: HTMLElement) => {
  if (!import.meta.env?.DEV || warnedAboutMissingScrollY) return
  // No `.ant-table` ancestor means this is not a table at all, and the fallback
  // is simply where the menu belongs.
  if (!trigger.closest(".ant-table")) return

  warnedAboutMissingScrollY = true
  console.warn(
    "[RowActionsMenu] This table renders a row actions menu but sets no " +
      "`scroll.y`, so AntD gives it no `.ant-table-body` to anchor the menu " +
      "in. The menu will hang outside the table and over any sticky chrome. " +
      "Give the table a `scroll.y`."
  )
}

/**
 * The row height AntD lays a dropdown's items out at, for a caller that cannot
 * reach `theme.useToken()`.
 *
 * A column renderer is not a component, so it has no hook to read the live
 * theme with. This resolves the default seed rather than whatever a
 * `ConfigProvider` above it may have overridden; `RowActionsMenu` passes the
 * live value instead.
 *
 * Memoised because `getDesignToken` is uncached and regenerates the whole
 * palette — ~1.9ms to read one number — and rc-portal re-runs `getPopupContainer`
 * on every render of an opened Dropdown, not once per open.
 */
let cachedItemHeight: number | undefined
const defaultItemHeight = () =>
  (cachedItemHeight ??= theme.getDesignToken().controlHeightSM)

/**
 * The `getPopupContainer` a row's actions menu needs, ready to hand to AntD.
 *
 * The height is settled here rather than inside the returned callback: the items
 * are already fixed by the time the column renders, and the callback runs on
 * every render of an opened Dropdown.
 */
export const rowActionsPopupContainer = (
  items: MenuItems,
  itemHeight?: number
) => {
  const menuHeight = estimateMenuHeight(
    items,
    itemHeight ?? defaultItemHeight()
  )
  return (trigger: HTMLElement): HTMLElement =>
    getRowActionsPopupContainer(trigger, menuHeight)
}

/**
 * Everything a row's actions menu needs on its `Dropdown`, as one object.
 *
 * `RowActionsMenu` covers the "⋯" trigger and spreads this itself. A column
 * whose affordance is something else — an icon button in a row of them, under
 * its own tooltip — cannot adopt that component without changing how the row
 * looks, and takes this instead. Bundled because the two halves are silently
 * half-usable apart: the class alone caps the z-index without anchoring, and the
 * container alone anchors without capping the `document.body` fallback.
 */
export const rowActionsDropdownProps = (
  items: MenuItems,
  itemHeight?: number
) => ({
  overlayClassName: ROW_ACTIONS_MENU_CLASS,
  getPopupContainer: rowActionsPopupContainer(items, itemHeight)
})
